import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Banknote, CreditCard, MessageCircle, Lock, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";
import { api, errMsg, imgUrl } from "@/lib/api";
import { Mark } from "@/components/Logo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const field = "w-full border-0 border-b border-[#241914]/25 bg-transparent px-0 py-3 text-base text-[#0B0B0C] outline-none transition-colors placeholder:text-[#241914]/40 focus:border-[#C6A76A]";

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.18em] text-[#241914]/70 rtl:tracking-normal rtl:text-sm">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-[#9b2c2c]">{error}</span>}
    </label>
  );
}

function Summary({ cityFee, hasCity }) {
  const { t, tr, fmt } = useLang();
  const { items, subtotal } = useCart();
  return (
    <div className="bg-[#0B0B0C] p-7 text-[#F7F3EB] sm:p-10" data-testid="checkout-summary">
      <p className="eyebrow">{t("order_summary")}</p>
      <div className="mt-6 divide-y divide-[#F7F3EB]/10">
        {items.map((i) => (
          <div key={i.product_id} className="flex items-center gap-4 py-4">
            <div className="arch-sm h-16 w-12 shrink-0 overflow-hidden bg-[#141011]">{i.image ? <img src={imgUrl(i.image)} alt="" className="h-full w-full object-cover" /> : <Mark className="m-auto mt-4 h-7 w-6 text-[#C6A76A]/60" />}</div>
            <div className="flex-1">
              <p className="font-display text-lg leading-tight">{tr(i.name)}</p>
              <p className="font-latin text-xs text-[#F7F3EB]/60">{i.volume} × {i.qty}</p>
            </div>
            <span className="font-latin text-sm rtl:font-sans">{fmt(i.price * i.qty)}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 space-y-3 border-t border-[#F7F3EB]/15 pt-6 text-sm">
        <div className="flex justify-between"><span className="text-[#F7F3EB]/70">{t("subtotal")}</span><span className="font-latin rtl:font-sans" data-testid="checkout-subtotal">{fmt(subtotal)}</span></div>
        <div className="flex justify-between">
          <span className="text-[#F7F3EB]/70">{t("delivery")}</span>
          <span className="font-latin rtl:font-sans" data-testid="checkout-delivery-fee-display">{hasCity ? (cityFee > 0 ? fmt(cityFee) : t("free")) : "—"}</span>
        </div>
        <div className="flex items-baseline justify-between border-t border-[#F7F3EB]/15 pt-5">
          <span className="font-display text-xl">{t("total")}</span>
          <span className="font-display text-3xl text-[#C6A76A]" data-testid="checkout-total">{fmt(subtotal + (hasCity ? cityFee : 0))}</span>
        </div>
      </div>
      <p className="mt-6 flex items-center gap-2 text-xs text-[#F7F3EB]/60"><Lock className="h-3.5 w-3.5" strokeWidth={1.3} /> {t("secure_note")}</p>
    </div>
  );
}

export default function Checkout() {
  const { t, lang, rtl } = useLang();
  const { items, clear, syncWith } = useCart();
  const { cities, settings, products, loaded } = useStore();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", address: "", notes: "" });
  const [cityId, setCityId] = useState("");
  const [method, setMethod] = useState("");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loaded) syncWith(products);
  }, [loaded, products, syncWith]);

  const methods = useMemo(
    () =>
      [
        settings?.cod_enabled && { id: "cod", icon: Banknote, label: t("svc_cod_t"), desc: t("svc_cod_d") },
        settings?.card_enabled && { id: "card", icon: CreditCard, label: t("svc_card_t"), desc: t("svc_card_d") },
        settings?.whatsapp_enabled && settings?.whatsapp_number && { id: "whatsapp", icon: MessageCircle, label: t("svc_wa_t"), desc: t("svc_wa_d") },
      ].filter(Boolean),
    [settings, t]
  );

  useEffect(() => {
    if (!method && methods.length) setMethod(methods[0].id);
  }, [methods, method]);

  const city = cities.find((c) => c.id === cityId);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = t("required");
    if (form.phone.replace(/\D/g, "").length < 9) e.phone = t("invalid_phone");
    if (form.address.trim().length < 4) e.address = t("required");
    if (!cityId) e.city = t("required");
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async () => {
    if (!validate() || !method) return;
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        city_id: cityId,
        customer: form,
        payment_method: method,
        origin_url: window.location.origin,
        lang,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      clear();
      nav(`/order/${data.order.public_token}`, { state: { justPlaced: true } });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" && d.startsWith("OUT_OF_STOCK") ? t("out_of_stock_err") : errMsg(e, t("error_generic")));
      setBusy(false);
    }
  };

  const Back = rtl ? ArrowRight : ArrowLeft;

  if (!items.length) {
    return (
      <main className="flex min-h-[80vh] flex-col items-center justify-center gap-6 bg-[#F7F3EB] px-5 pt-24 text-center" data-testid="checkout-empty">
        <Mark className="h-16 w-14 text-[#C6A76A]" />
        <p className="font-display text-3xl">{t("bag_empty")}</p>
        <Link to="/#collection" data-testid="checkout-back-collection" className="link-line pb-1 text-sm tracking-[0.2em] rtl:tracking-normal">{t("cta_explore")}</Link>
      </main>
    );
  }

  const submitLabel = method === "card" ? t("pay_now") : method === "whatsapp" ? t("send_wa") : t("place_order");

  return (
    <main className="min-h-screen bg-[#F7F3EB] pt-[72px] text-[#0B0B0C]" data-testid="checkout-page">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-10 px-5 py-12 sm:px-10 lg:grid-cols-12 lg:gap-16 lg:py-20">
        <div className="lg:col-span-7">
          <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] text-[#241914]/70 hover:text-[#0B0B0C] rtl:tracking-normal rtl:text-sm" data-testid="checkout-back-link">
            <Back className="h-3.5 w-3.5" /> {t("back")}
          </Link>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl">{t("co_title")}</h1>

          <section className="mt-12">
            <p className="eyebrow !text-[#8a6d35]">01 — {t("co_delivery_info")}</p>
            <div className="mt-6 grid grid-cols-1 gap-7 sm:grid-cols-2">
              <Field label={t("full_name")} error={errors.full_name}>
                <input data-testid="checkout-name-input" className={field} value={form.full_name} onChange={set("full_name")} autoComplete="name" />
              </Field>
              <Field label={t("phone")} error={errors.phone}>
                <input data-testid="checkout-phone-input" className={field} value={form.phone} onChange={set("phone")} inputMode="tel" autoComplete="tel" dir="ltr" placeholder="06 00 00 00 00" />
              </Field>
              <Field label={t("city")} error={errors.city}>
                {cities.length ? (
                  <Select value={cityId} onValueChange={setCityId}>
                    <SelectTrigger data-testid="checkout-city-select" className="mt-1 h-auto rounded-none border-0 border-b border-[#241914]/25 bg-transparent px-0 py-3 text-base shadow-none focus:ring-0">
                      <SelectValue placeholder={t("choose_city")} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72 rounded-none bg-[#F7F3EB]">
                      {cities.map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`checkout-city-option-${c.id}`}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="mt-3 text-sm text-[#9b2c2c]" data-testid="checkout-no-cities">{t("no_cities")}</p>
                )}
                {city?.eta && <span className="mt-1 block text-xs text-[#241914]/60">{city.eta}</span>}
              </Field>
              <Field label={t("email_opt")}>
                <input data-testid="checkout-email-input" className={field} value={form.email} onChange={set("email")} type="email" autoComplete="email" dir="ltr" />
              </Field>
              <div className="sm:col-span-2">
                <Field label={t("address")} error={errors.address}>
                  <input data-testid="checkout-address-input" className={field} value={form.address} onChange={set("address")} placeholder={t("address_ph")} autoComplete="street-address" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label={t("notes_opt")}>
                  <input data-testid="checkout-notes-input" className={field} value={form.notes} onChange={set("notes")} />
                </Field>
              </div>
            </div>
          </section>

          <section className="mt-14">
            <p className="eyebrow !text-[#8a6d35]">02 — {t("payment")}</p>
            {methods.length === 0 ? (
              <p className="mt-6 text-sm text-[#9b2c2c]" data-testid="checkout-no-payment">{t("no_payment")}</p>
            ) : (
              <div className="mt-6 grid grid-cols-1 gap-3">
                {methods.map((m) => (
                  <button
                    key={m.id}
                    data-testid={`checkout-payment-method-${m.id}`}
                    onClick={() => setMethod(m.id)}
                    className={`flex items-center gap-5 border p-5 text-start transition-colors duration-300 ${method === m.id ? "border-[#0B0B0C] bg-[#0B0B0C] text-[#F7F3EB]" : "border-[#241914]/20 hover:border-[#241914]/50"}`}
                  >
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${method === m.id ? "border-[#C6A76A]" : "border-[#241914]/40"}`}>
                      {method === m.id && <span className="h-2.5 w-2.5 rounded-full bg-[#C6A76A]" />}
                    </span>
                    <m.icon className={`h-5 w-5 ${method === m.id ? "text-[#C6A76A]" : "text-[#8a6d35]"}`} strokeWidth={1.2} />
                    <span>
                      <span className="block text-base">{m.label}</span>
                      <span className={`block text-xs ${method === m.id ? "text-[#F7F3EB]/70" : "text-[#241914]/65"}`}>{m.desc}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>

          <button
            data-testid="checkout-submit-order-btn"
            onClick={submit}
            disabled={busy || !methods.length || !cities.length}
            className="mt-12 flex w-full items-center justify-center gap-3 bg-[#0B0B0C] py-5 text-[0.8rem] tracking-[0.25em] text-[#F7F3EB] transition-colors duration-500 hover:bg-[#241914] disabled:opacity-40 rtl:text-base rtl:tracking-normal"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitLabel}
          </button>
        </div>
        <div className="order-first lg:order-last lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <Summary cityFee={Number(city?.fee ?? 0)} hasCity={!!city} />
          </div>
        </div>
      </div>
    </main>
  );
}
